import { useState, useEffect } from "react";
import { Grid, TextField, Button } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import CustomTableNew from "../components/CustomTableNew.jsx";
import axios from "../customAxios.js";
import LoadingComponent from "../components/LoadingComponent.jsx";
import { getImagesUrls } from "../utils/s3Utils.js";
import { useNavigate } from "react-router-dom";

function SellerInventory() {
  const navigate = useNavigate();

  //values to control pagination of the component table
  const [page, setPage] = useState(
    localStorage.getItem("sellerInvenotryFilter")
      ? JSON.parse(localStorage.getItem("sellerInvenotryFilter")).page
      : 0
  );
  const [rowsPerPage, setRowsPerPage] = useState(
    localStorage.getItem("sellerInvenotryFilter")
      ? JSON.parse(localStorage.getItem("sellerInvenotryFilter")).rowsPerPage
      : 20
  );

  //data that will be set on every page change and filter set
  const [pageData, setPageData] = useState();
  const [loadingPageData, setLoadingPageData] = useState(true);

  //aux values for page data before adapting it to the table
  // const [auxPageData, setAuxPageData] = useState([]);

  //filter values of the table
  const [search, setSearch] = useState(
    localStorage.getItem("sellerInvenotryFilter")
      ? JSON.parse(localStorage.getItem("sellerInvenotryFilter")).search
      : ""
  );

  //aux values for delay on text search fields
  const [auxSearcher, setAuxSearcher] = useState(
    localStorage.getItem("sellerInvenotryFilter")
      ? JSON.parse(localStorage.getItem("sellerInvenotryFilter")).search
      : ""
  );

  //useState to count the total number of assignments filtered by backend
  const [inventoryCount, setInventoryCount] = useState(0);

  //useState to catch when general data is already fetched
  const [generalParamsFetched, setGeneralParamsFetched] = useState(false);

  //initial render flag for pagination settings
  const [isInitialRender, setIsInitialRender] = useState(true);

  const [products, setProducts] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [allImagesLoaded, setAllImagesLoaded] = useState(false);
  const [timeoutReached, setTimeoutReached] = useState(false);

  //useState use to get images urls
  const [imageskeyRoutes, setImagesKeyRoutes] = useState({});
  const [imageUrls, setImageUrls] = useState({});

  //set dataForTable when imageUrls are set
  useEffect(() => {
    if (Object.keys(imageUrls).length > 0) {
      const dataForTableConst = products.map((product) => {
        return {
          id: product.id,
          sku: product.sku,
          name: product.name,
          picture_url: imageUrls[product.id],
          ideal_stock: product.total_ideal_stock,
        };
      });
      setPageData(dataForTableConst);
    }
  }, [imageUrls]);

  //set image urls when images key routes are set
  useEffect(() => {
    async function getUrls() {
      const urls = await getImagesUrls(
        imageskeyRoutes,
        `inventory-seller-${search}-${page}-${rowsPerPage}`
      );
      setImageUrls(urls);
    }
    //just run this function when images key routes are already set
    if (Object.keys(imageskeyRoutes).length > 0) {
      getUrls();
    }
  }, [imageskeyRoutes]);

  //USE EFFECT TO DETECT WHEN ALL IMAGES ARE LOADED
  useEffect(() => {
    // if imageUrls in not empty
    if (Object.keys(imageUrls).length > 0) {
      const imageElements = Object.keys(imageUrls).map((key) => {
        const image = new Image();
        image.src = imageUrls[key];
        return image;
      });

      const checkAllImagesLoaded = () => {
        const allLoaded = imageElements.every((image) => image.complete);
        if (allLoaded) {
          setAllImagesLoaded(true);
        }
      };

      imageElements.forEach((image) => {
        image.addEventListener("load", checkAllImagesLoaded);
      });

      return () => {
        // Remove event listeners on component unmount
        imageElements.forEach((image) => {
          image.removeEventListener("load", checkAllImagesLoaded);
        });
      };
    }
  }, [imageUrls]);

  //USE EFFECT TO DETECT WHEN TIMEOUT IS REACHED, IF IMAGES ARE NOT LOADED
  useEffect(() => {
    let timeoutId;

    // Set a timeout to check if images are not loaded within 10000 milliseconds (10 seconds)
    timeoutId = setTimeout(() => {
      // If images are not loaded, set timeoutReached to true
      if (!allImagesLoaded) {
        setTimeoutReached(true);
      }
    }, 5000);

    // Cleanup: Clear the timeout on unmount
    return () => {
      clearTimeout(timeoutId);
    };
  }, [allImagesLoaded]);

  const searchParams = () => {
    return {
      search: search,
    };
  };

  //function to fetch all general parameters that do not depends on the page and rowsPerPage
  async function fetchGeneralParams() {
    try {
      const { data } = await axios.get("products/seller_products/general_params/", { params: searchParams() })
      setInventoryCount(data.number_of_products);
      setGeneralParamsFetched(true);
    } catch (error) {
      console.log(`error: ${error}`);
    }
  }

  useEffect(() => {
    fetchGeneralParams();
  }, [search]);

  //set page to 0 when necessary
  useEffect(() => {
    //if localStorage "invenotryWarehouseFilter" does not exist, set page to 0
    if (!localStorage.getItem("sellerInvenotryFilter")) {
      setPage(0);
    }
    //if is on useEffect not on initial render, set page 0
    if (isInitialRender) {
      setIsInitialRender(false);
    } else {
      setPage(0);
    }
  }, [search, rowsPerPage]);

  //load page data on changes
  useEffect(() => {
    if (!loadingPageData) {
      setLoadingPageData(true);
    }
    fetchProducts();
    //save on localStorage the filter and page values of orders
    localStorage.setItem(
      "sellerInvenotryFilter",
      JSON.stringify({ search, page, rowsPerPage })
    );
  }, [search, rowsPerPage, page]);

  //useEffect to catch auxSearcher changes, and triger a function every 2 seconds that dont change
  useEffect(() => {
    // Delayed search function
    const delayedSearch = (value) => {
      // Your search logic goes here
      setSearch(value);
    };
    if (!loadingPageData) {
      setLoadingPageData(true);
    }
    // Timer variable for setTimeout
    let timer;

    // Set a delay of 2 seconds before executing the search
    const delay = 1500;

    // Clear the previous timer when auxSearcher changes
    clearTimeout(timer);

    // Set a new timer for delayed search
    timer = setTimeout(() => {
      delayedSearch(auxSearcher);
    }, delay);

    // Clean up the timer on component unmount or when auxSearcher changes
    return () => {
      clearTimeout(timer);
    };
  }, [auxSearcher]);

  const fetchProducts = async () => {
    setLoadingPageData(true);
    setError(false);
    const search_params_fetch_products = {
      search: search,
      page: page,
      rowsPerPage: rowsPerPage,
    };
    try {
      const { data } = await axios.get("products/seller_products/", {
        params: search_params_fetch_products,
      });
      setProducts(data);

      const imageskeyRoutes = {};
      data.forEach((product) => {
        imageskeyRoutes[product.id] = product.picture_url_key;
      });
      setImagesKeyRoutes(imageskeyRoutes);
      console.log("Productos cargados correctamente");
    } catch (error) {
      setProducts([]);
      setPageData([]);
      setError(true);
      console.log("Error al cargar los productos", error);
    } finally {
      setLoadingPageData(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const headerTable = [
    { columnName: "PRODUCTO", dbName: "name_sku_picture", enabled: true },
    { columnName: "CANALES ACTIVOS", dbName: "canals", enabled: true },
    { columnName: "STOCK", dbName: "ideal-stock", enabled: true },
    { columnName: "DETALLES", dbName: "details", enabled: true },
  ];

  if (allImagesLoaded || timeoutReached) {
    return (
      <>
        <Grid item xs={12}>
          <h1>Inventario</h1>
        </Grid>

        <Grid item xs={12} sm className="filters-container">
          <div className="row-container">
            <Grid item xs={6} sm="auto" className="input-service-filter">
              <TextField
                variant="outlined"
                label="Buscar"
                name="search"
                value={auxSearcher}
                onChange={(e) => {
                  setAuxSearcher(e.target.value);
                }}
                className="mg-l-10"
              />
            </Grid>
            <Grid item xs={6} sm="auto" className="input-service-filter">
              <Button
                variant="outlined"
                component="label"
                color="primary"
                className="mg-l-10"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/home/seller-create-product");
                }}
              >
                Crear productos
              </Button>
            </Grid>
          </div>
        </Grid>
        <br />
        {loading ? (
          <CircularProgress color="secondary" />
        ) : error ? (
          <h1>Hubo un error al cargar el inventario</h1>
        ) : (
          <Grid container className="custom-table-container">
            <Grid item xs={12} className="flex-center mg-t-0">
              <CustomTableNew
                totalCount={inventoryCount}
                page={page}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                pageData={pageData}
                setPageData={setPageData}
                loadingPageData={loadingPageData}
                headerTable={headerTable}
                selected={"none"}
                setSelected={"none"}
                openCollapseButton={true}
                openCollapseTooltip={"Ver más"}
                openCollapseIcon={
                  <i className="material-icons-outlined icon-actions-panel-test">
                    add_circle_outline
                  </i>
                }
                collapseType={"products-inventory-seller"}
                hasPagination={true}
                editable={true}
              />
            </Grid>
          </Grid>
        )}
      </>
    );
  } else {
    return <LoadingComponent />;
  }
}

export default SellerInventory;
